// Order / payment / delivery legacy-parity patches (gap IDs S5, S6, S8, S9, S10, S12, S14).
//
// Applied after 10-orders.ts (order editor + list columns): this file only ADDS
// row actions / detail documents to sales, purchases, quotations and return
// orders, and owns the payments, deliveries and POS held-orders configs.

import { createElement } from 'react';

import {
  addPaymentAction,
  createSaleFromQuotationAction,
  deliveryActions,
  downloadAction,
  duplicateAction,
  emailAction,
  listPaymentsAction,
  printAction,
  receiptAction,
  reviewPaymentAction,
  toggleVoidActions
} from '../../features/order-actions/actions';
import { DeliveryCustomForm, DELIVERY_STATUSES } from '../../features/order-actions/DeliveryForm';
import { GatewaySelect, GiftCardInput, MonthInput, ONLINE_CARD_GATEWAYS, RemoteSelect } from '../../features/order-actions/fields';
import { documentDetailExtra } from '../../features/documents/DocumentPanel';
import { hasTranslation, t } from '../../i18n';
import { moneyValue, partyName, relationName, simpleDetailSections, yesNoValue } from '../configHelpers';
import type { CrudField, CrudRecord, CrudResourceConfig, CrudRowAction } from '../types';

type Patch = (base: CrudResourceConfig | undefined) => CrudResourceConfig;

// Keep the base config (or a minimal shell if a previous override removed it).
function ensure(base: CrudResourceConfig | undefined, resource: string, title: string, singular: string): CrudResourceConfig {
  return base ?? { resource, title, singular, orderBy: 'created_at desc', columns: [], fields: [] };
}

// Append actions, skipping keys a previous override already defined.
function withActions(base: CrudResourceConfig, actions: CrudRowAction[]): CrudRowAction[] {
  const existing = base.rowActions ?? [];
  const keys = new Set(existing.map((action) => action.key));
  return [...existing, ...actions.filter((action) => !keys.has(action.key))];
}

const attachmentsCount = (row: CrudRecord) => {
  const count = Array.isArray(row.attachments) ? row.attachments.length : 0;
  return count ? String(count) : '';
};

const gatewayText = (value: unknown) => {
  const gateway = String(value ?? '');
  if (!gateway) return t('orderActions.gateway.requesting');
  return hasTranslation(`orderActions.gateway.${gateway}`) ? t(`orderActions.gateway.${gateway}`) : gateway;
};

const deliveryStatusText = (value: unknown) => {
  const status = String(value ?? '');
  return hasTranslation(`orderActions.deliveryStatus.${status}`) ? t(`orderActions.deliveryStatus.${status}`) : status || '-';
};

// --- Payments (S8 / S9) -----------------------------------------------------

const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.pdf,.zip';
const isOnlineCard = (form: Record<string, unknown>) => ONLINE_CARD_GATEWAYS.includes(String(form.gateway ?? ''));

const paymentFields: CrudField[] = [
  {
    name: 'type',
    label: 'Type',
    type: 'select',
    required: true,
    defaultValue: 'customer',
    options: [
      { value: 'customer', label: t('orderActions.party.customer') },
      { value: 'supplier', label: t('orderActions.party.supplier') }
    ],
    section: 'Details'
  },
  {
    name: 'customer_id',
    label: 'Customer',
    type: 'custom',
    required: true,
    section: 'Details',
    visibleWhen: (form) => form.type !== 'supplier',
    render: (props) => createElement(RemoteSelect, { props, searchEndpoint: 'customers/search', detailEndpoint: 'customers', labelField: '_customer_label' })
  },
  {
    name: 'supplier_id',
    label: 'Supplier',
    type: 'custom',
    required: true,
    section: 'Details',
    visibleWhen: (form) => form.type === 'supplier',
    render: (props) => createElement(RemoteSelect, { props, searchEndpoint: 'suppliers/search', detailEndpoint: 'suppliers', labelField: '_supplier_label' })
  },
  { name: 'reference', label: 'Reference', section: 'Details' },
  { name: 'account_id', label: 'Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Details' },
  { name: 'amount', label: 'Amount', type: 'number', required: true, section: 'Details' },
  {
    name: 'gateway',
    label: 'Gateway',
    type: 'custom',
    defaultValue: '',
    section: 'Gateway',
    helperText: t('orderActions.gatewayHelper'),
    render: (props) => createElement(GatewaySelect, { props })
  },
  { name: 'cheque_number', label: 'Cheque Number', required: true, section: 'Gateway', visibleWhen: (form) => form.gateway === 'cheque' },
  {
    name: 'gift_card_number',
    label: 'Gift Card Number',
    type: 'custom',
    section: 'Gateway',
    visibleWhen: (form) => form.gateway === 'gift_card',
    render: (props) => createElement(GiftCardInput, { props })
  },
  { name: 'card_holder', label: 'Card Holder', required: true, section: 'Gateway', visibleWhen: (form) => form.gateway === 'credit_card' },
  { name: 'firstName', label: 'First Name', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'lastName', label: 'Last Name', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'billingAddress1', label: 'Billing Address', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'billingCity', label: 'Billing City', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'billingPostcode', label: 'Billing Postcode', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'billingState', label: 'Billing State', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'billingCountry', label: 'Billing Country', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'card_number', label: 'Card Number', required: true, section: 'Gateway', visibleWhen: (form) => form.gateway === 'credit_card' || isOnlineCard(form) },
  {
    name: 'expiry_date',
    label: 'Expiry Date',
    type: 'custom',
    required: true,
    section: 'Gateway',
    visibleWhen: (form) => form.gateway === 'credit_card' || isOnlineCard(form),
    render: (props) => createElement(MonthInput, { props })
  },
  { name: 'cvv', label: 'CVV', required: true, section: 'Gateway', visibleWhen: isOnlineCard },
  { name: 'attachments', label: 'Attachments', type: 'files', accept: ATTACHMENT_ACCEPT, section: 'Notes' },
  { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
];

const CARD_KEYS = ['firstName', 'lastName', 'billingAddress1', 'billingCity', 'billingPostcode', 'billingState', 'billingCountry', 'cvv'];

// Mirrors the legacy PaymentFormComponent submit + PaymentRequest rules. The
// `received` flag is NOT sent: like legacy, PaymentRequest::validated() sets
// received=1 for supplier payments and cash/other gateways (journal posting),
// everything else is a payment request (received=0) until reviewed.
export function normalizePaymentPayload(payload: Record<string, unknown>) {
  const type = payload.type === 'supplier' ? 'supplier' : 'customer';
  const gateway = String(payload.gateway ?? '');
  const amount = Number(payload.amount ?? 0);
  const max = Number(payload.max_amount ?? 0);
  if (max > 0 && amount > max) throw new Error(t('orderActions.amountMaxError', { max: moneyValue(max) }));
  const out: Record<string, unknown> = {
    type,
    amount,
    gateway,
    account_id: payload.account_id,
    reference: payload.reference ?? '',
    details: payload.details ?? '',
    customer_id: type === 'customer' ? payload.customer_id || '' : '',
    supplier_id: type === 'supplier' ? payload.supplier_id || '' : ''
  };
  ['sale_id', 'purchase_id', 'return_id'].forEach((key) => {
    if (payload[key]) out[key] = payload[key];
  });
  if (gateway === 'cheque') out.cheque_number = payload.cheque_number ?? '';
  if (gateway === 'gift_card') out.gift_card_number = payload.gift_card_number ?? '';
  if (gateway === 'credit_card' || ONLINE_CARD_GATEWAYS.includes(gateway)) {
    out.card_number = payload.card_number ?? '';
    out.expiry_date = payload.expiry_date ? String(payload.expiry_date).slice(0, 7) : '';
  }
  if (gateway === 'credit_card') out.card_holder = payload.card_holder ?? '';
  if (ONLINE_CARD_GATEWAYS.includes(gateway)) CARD_KEYS.forEach((key) => (out[key] = payload[key] ?? ''));
  if (payload.attachments) out.attachments = payload.attachments;
  return out;
}

function normalizePaymentAfterLoad(record: CrudRecord): CrudRecord {
  const supplier = String(record.payable_type ?? '').includes('Supplier');
  const payable = (record.payable ?? {}) as { id?: unknown; name?: unknown };
  return {
    ...record,
    amount: Number(record.amount ?? 0),
    gateway: record.gateway ?? '',
    type: supplier ? 'supplier' : 'customer',
    customer_id: supplier ? '' : payable.id ?? record.payable_id ?? '',
    supplier_id: supplier ? payable.id ?? record.payable_id ?? '' : '',
    _customer_label: supplier ? '' : String(payable.name ?? ''),
    _supplier_label: supplier ? String(payable.name ?? '') : '',
    expiry_date: record.expiry_date ? String(record.expiry_date).slice(0, 7) : ''
  };
}

const payments: Patch = (input) => {
  const base = ensure(input, 'payments', 'Payments', 'Payment');
  return {
    ...base,
    searchMode: 'server',
    searchField: 'reference',
    requiresLocation: true,
    // customFields intentionally off: PaymentRequest drops extra_attributes and the
    // Payment model has no schemaless cast, so legacy never persisted them either.
    // Existing values still show in the drawer (ExtraAttributesSection / document).
    bulkDelete: true,
    showAttachments: true,
    pageSizes: [10, 25, 50, 100],
    columns: [
      { key: 'created_at', label: 'Created At', sortable: true },
      { key: 'reference', label: 'Reference', sortable: true },
      { key: 'amount', label: 'Amount', align: 'right', render: (row) => moneyValue(row.amount) },
      { key: 'attachments', label: 'Attachments', align: 'center', render: attachmentsCount },
      { key: 'gateway', label: 'Gateway', render: (row) => gatewayText(row.gateway) },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account, '') },
      { key: 'payable', label: 'Created For', render: partyName },
      { key: 'user', label: 'Created By', render: (row) => relationName(row.user, '') },
      { key: 'received', label: 'Received', sortable: true, render: (row) => yesNoValue(row.received), tone: (row) => (Number(row.received) === 1 ? 'success' : 'warning') },
      { key: 'details', label: 'Details' }
    ],
    listFilters: [
      ...(base.listFilters ?? []).filter((filter) => filter.name !== 'review' && filter.name !== 'due'),
      { name: 'review', label: 'orderActions.filterReview', type: 'toggle', onValue: '1' },
      { name: 'due', label: 'orderActions.filterDue', type: 'toggle', onValue: '1' }
    ],
    fields: paymentFields,
    normalizeBeforeSave: normalizePaymentPayload,
    normalizeAfterLoad: normalizePaymentAfterLoad,
    detailExtra: documentDetailExtra('payment', base.detailExtra),
    rowActions: withActions(base, [
      printAction('payments', 'payment', 'read-payments'),
      emailAction('payments', 'email-payments'),
      downloadAction('payments', 'read-payments'),
      reviewPaymentAction()
    ])
  };
};

// --- Sales / purchases / quotations / return orders (S5 / S6 / S12) ---------

const sales: Patch = (input) => {
  const base = ensure(input, 'sales', 'Sales', 'Sale');
  return {
    ...base,
    showAttachments: base.showAttachments ?? true,
    rowTone: base.rowTone ?? ((row) => (Number(row.void) === 1 ? 'error' : undefined)),
    detailExtra: documentDetailExtra('sale', base.detailExtra),
    rowActions: withActions(base, [
      receiptAction(),
      printAction('sales', 'sale', 'read-sales'),
      emailAction('sales', 'email-sales'),
      downloadAction('sales', 'read-sales'),
      duplicateAction('sales'),
      addPaymentAction('sale'),
      listPaymentsAction('sales'),
      ...deliveryActions(),
      ...toggleVoidActions('sales')
    ])
  };
};

const purchases: Patch = (input) => {
  const base = ensure(input, 'purchases', 'Purchases', 'Purchase');
  return {
    ...base,
    showAttachments: base.showAttachments ?? true,
    rowTone: base.rowTone ?? ((row) => (Number(row.void) === 1 ? 'error' : undefined)),
    detailExtra: documentDetailExtra('purchase', base.detailExtra),
    rowActions: withActions(base, [
      printAction('purchases', 'purchase', 'read-purchases'),
      emailAction('purchases', 'email-purchases'),
      downloadAction('purchases', 'read-purchases'),
      duplicateAction('purchases'),
      addPaymentAction('purchase'),
      listPaymentsAction('purchases'),
      ...toggleVoidActions('purchases')
    ])
  };
};

const quotations: Patch = (input) => {
  const base = ensure(input, 'quotations', 'Quotations', 'Quotation');
  return {
    ...base,
    // Quotations have no status column in the legacy list (nor in the table).
    columns: base.columns.filter((column) => column.key !== 'status'),
    showAttachments: base.showAttachments ?? true,
    detailExtra: documentDetailExtra('quotation', base.detailExtra),
    rowActions: withActions(base, [
      printAction('quotations', 'quotation', 'read-quotations'),
      emailAction('quotations', 'email-quotations'),
      downloadAction('quotations', 'read-quotations'),
      createSaleFromQuotationAction()
    ])
  };
};

const returnOrders: Patch = (input) => {
  const base = ensure(input, 'return_orders', 'Return Orders', 'Return Order');
  return {
    ...base,
    showAttachments: base.showAttachments ?? true,
    detailExtra: documentDetailExtra('return_order', base.detailExtra),
    rowActions: withActions(base, [
      printAction('return_orders', 'return_order', 'read-return-orders'),
      emailAction('return_orders', 'email-return-orders'),
      downloadAction('return_orders', 'read-return-orders'),
      addPaymentAction('return')
    ])
  };
};

// --- Deliveries (S10) --------------------------------------------------------

const deliveries: Patch = (input) => {
  const base = ensure(input, 'deliveries', 'Deliveries', 'Delivery');
  return {
    ...base,
    // Creation only happens from a sale (sales row action → POST sales/{id}/deliveries).
    disableCreate: true,
    showAttachments: true,
    columns: [
      { key: 'created_at', label: 'Created At', sortable: true },
      { key: 'date', label: 'Date', sortable: true },
      { key: 'reference', label: 'Reference', sortable: true },
      { key: 'customer', label: 'Customer', render: (row) => relationName(row.customer, '') },
      { key: 'status', label: 'Status', sortable: true, render: (row) => deliveryStatusText(row.status), tone: (row) => (row.status === 'delivered' ? 'success' : row.status === 'delivering' ? 'warning' : 'default') },
      { key: 'user', label: 'Created By', render: (row) => relationName(row.user, '') },
      { key: 'attachments', label: 'Attachments', align: 'center', render: attachmentsCount },
      { key: 'delivered_by', label: 'Delivered By' },
      { key: 'delivered_at', label: 'Delivered At', sortable: true },
      { key: 'details', label: 'Details' }
    ],
    detailSections: base.detailSections ?? simpleDetailSections,
    // The custom form (edit) re-sends customer_id + items, which DeliveryRequest
    // requires on update; these generic fields only describe the record.
    fields: [
      { name: 'date', label: 'Date', type: 'date', required: true, section: 'Details' },
      { name: 'reference', label: 'Reference', section: 'Details' },
      { name: 'status', label: 'Status', type: 'select', required: true, defaultValue: 'preparing', options: DELIVERY_STATUSES.map((value) => ({ value, label: t(`orderActions.deliveryStatus.${value}`) })), section: 'Status' },
      { name: 'driver', label: 'Driver', section: 'Status' },
      { name: 'delivered_at', label: 'Delivered At', type: 'datetime', section: 'Delivery', visibleWhen: (form) => form.status === 'delivered' },
      { name: 'delivered_by', label: 'Delivered By', section: 'Delivery', visibleWhen: (form) => form.status === 'delivered' },
      { name: 'received_by', label: 'Received By', section: 'Delivery', visibleWhen: (form) => form.status === 'delivered' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ],
    customForm: DeliveryCustomForm,
    detailExtra: documentDetailExtra('delivery', base.detailExtra)
  };
};

// --- POS held orders (S14) ---------------------------------------------------

const orders: Patch = (input) => {
  const base = ensure(input, 'orders', 'Orders', 'Order');
  return {
    ...base,
    // Held POS carts: no create / edit from the admin; delete (single + bulk)
    // is reserved to the super role like the legacy OpenOrderListComponent.
    readOnly: false,
    disableCreate: true,
    disableUpdate: true,
    deleteSuperOnly: true,
    bulkDelete: true,
    columns: [
      { key: 'created_at', label: 'Created At', sortable: true },
      { key: 'oId', label: 'Ref Tab', sortable: true },
      { key: 'date', label: 'Date', sortable: true },
      { key: 'reference', label: 'Reference', sortable: true },
      { key: 'customer', label: 'Customer', render: (row) => relationName(row.customer, '') },
      { key: 'location', label: 'Location', render: (row) => relationName(row.location, '') },
      { key: 'user', label: 'Created By', render: (row) => relationName(row.user, '') },
      { key: 'total_items', label: 'Total Items', align: 'right' },
      { key: 'total_quantity', label: 'Total Quantity', align: 'right' },
      { key: 'grand_total', label: 'Grand Total', align: 'right', render: (row) => moneyValue(row.grand_total) },
      { key: 'details', label: 'Details' }
    ]
  };
};

export const overrides: Record<string, Patch> = {
  payments,
  sales,
  purchases,
  quotations,
  return_orders: returnOrders,
  deliveries,
  orders
};
